import {gql} from '@apollo/client';
import React from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ProcessRealtimeUpdateSubscriptionWrapper from 'processes/ProcessRealtimeUpdateSubscriptionWrapper';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {PROCESS_ROUTER_NODE_DETAILS_FRAGMENT} from '../../ProcessFragments';
import {
  ProcessFlowchartDataQuery,
  ProcessFlowchartDataQueryVariables,
} from './__generated/ProcessFlowchartDataQuery';
import {
  ProcessFlowchartStructureQuery,
  ProcessFlowchartStructureQueryVariables,
} from './__generated/ProcessFlowchartStructureQuery';

type ProcessFlowchartStructure = ExtractByTypename<
  ProcessFlowchartStructureQuery['processFlowcharts'][number],
  'ProcessFlowchartNode'
>;

export const PROCESS_FLOWCHART_STRUCTURE_QUERY = gql`
  query ProcessFlowchartStructureQuery($flowchartIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processFlowcharts(ids: $flowchartIds) {
      ... on ProcessFlowchartNode {
        id
        nodeConfigs {
          id
          name
          nodeDetails {
            ... on ProcessTaskNodeDetailsNode {
              id
              type
              schema {
                ... on ProcessTaskSchemaNode {
                  id
                  name
                }
                ...InaccessibleInfoGraphQL
              }
            }
            ... on ProcessRouterNodeDetailsNode {
              ...ProcessRouterNodeDetails
            }
            ... on ProcessRootNodeDetailsNode {
              ...ProcessRootNodeDetailsGraphQL
            }
            ... on ProcessOutputNodeDetailsNode {
              ...ProcessOutputNodeDetailsGraphQL
            }
            ... on ProcessEndNodeDetailsNode {
              ...ProcessEndNodeDetailsGraphQL
            }
          }
          inboundSchemaMappers {
            id
            toNodeConfig {
              id
            }
            schemaMapperConfig {
              id
            }
            schemaMapperDependencies {
              id
              fromNodeConfig {
                id
              }
            }
          }
        }
        edgeConfigs {
          id
          fromNodeConfig {
            id
          }
          toNodeConfig {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${PROCESS_ROUTER_NODE_DETAILS_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ProcessRootNodeDetails').fragment}
  ${getFragmentWrapper('ProcessOutputNodeDetails').fragment}
  ${getFragmentWrapper('ProcessEndNodeDetails').fragment}
`;

const EXECUTION_FLOWCHART_DATA_FRAGMENT = gql`
  fragment ExecutionFlowchartData on ProcessTaskExecutionFlowchartData {
    flowchartProcesses {
      id
      nodeConfig {
        id
      }
    }
    flowchartTasks {
      task {
        id
        process {
          id
        }
        status {
          id
          statusType
        }
        flowchartPredecessorNodes {
          id
        }
      }
      nodeConfig {
        id
        nodeDetails {
          ... on ProcessTaskNodeDetailsNode {
            id
            type
            schema {
              ... on ProcessTaskSchemaNode {
                id
                name
              }
              ...InaccessibleInfoGraphQL
            }
          }
          ... on ProcessRootNodeDetailsNode {
            ...ProcessRootNodeDetailsGraphQL
          }
          ... on ProcessRouterNodeDetailsNode {
            ...ProcessRouterNodeDetails
          }
          ... on ProcessOutputNodeDetailsNode {
            ...ProcessOutputNodeDetailsGraphQL
          }
          ... on ProcessEndNodeDetailsNode {
            ...ProcessEndNodeDetailsGraphQL
          }
        }
      }
    }
  }
`;

/**
 * This query is run after the one above. Starting with the tasks in the root process, it follows their
 * trails, retrieving all tasks that have a root task in this list. This is used to summarize all the
 * data for all tasks in a flowchart (for now, that means the statuses and the task groups).
 */
export const PROCESS_FLOWCHART_DATA_FRAGMENT = gql`
  fragment ProcessFlowchartDataInfo on ProcessTaskNode {
    id
    status {
      id
      statusType
    }
    outputs {
      id
      process {
        id
        nodeConfig {
          id
        }
      }
      flowchartPredecessorNodes {
        id
      }
    }
    isMappingInProgressForFlowchartRootedAtTask
    executionFlowchartData {
      ...ExecutionFlowchartData
    }
  }
  ${getFragmentWrapper('ProcessOutputNodeDetails').fragment}
  ${getFragmentWrapper('ProcessRootNodeDetails').fragment}
  ${getFragmentWrapper('ProcessEndNodeDetails').fragment}
  ${PROCESS_ROUTER_NODE_DETAILS_FRAGMENT}
  ${EXECUTION_FLOWCHART_DATA_FRAGMENT}
`;

export const PROCESS_FLOWCHART_DATA_QUERY = gql`
  query ProcessFlowchartDataQuery($processTaskIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTasks(ids: $processTaskIds) {
      ... on ProcessTaskNode {
        ...ProcessFlowchartDataInfo
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${PROCESS_FLOWCHART_DATA_FRAGMENT}
`;

export function NonPollingFlowchartWrapper(props: {
  flowchartIds: Array<string>;
  processTaskIds: Array<string>;
  refetchParent: () => Promise<unknown>;
  children: (
    flowcharts: Array<ProcessFlowchartStructure>,
    flowchartData: ProcessFlowchartDataQuery,
    forceRefetch: () => void
  ) => JSX.Element | null;
}): JSX.Element | null {
  const flowchartStructureResults = useBenchlingQuery<
    ProcessFlowchartStructureQuery,
    ProcessFlowchartStructureQueryVariables
  >(PROCESS_FLOWCHART_STRUCTURE_QUERY, {
    renderCachedDataWhenRefetching: true,
    variables: {flowchartIds: props.flowchartIds},
  });

  const flowchartDataResults = useBenchlingQuery<
    ProcessFlowchartDataQuery,
    ProcessFlowchartDataQueryVariables
  >(PROCESS_FLOWCHART_DATA_QUERY, {
    variables: {processTaskIds: props.processTaskIds},
    renderCachedDataWhenRefetching: true,
    fetchPolicy: 'network-only',
  });

  if (
    flowchartStructureResults.type !== QueryResultType.SUCCESS ||
    flowchartDataResults.type !== QueryResultType.SUCCESS
  ) {
    return <CenteredHorizontalThrobber />;
  }

  const flowcharts = flowchartStructureResults.data.processFlowcharts.filter(
    (flowchart): flowchart is ProcessFlowchartStructure => !isInaccessible(flowchart)
  );

  return props.children(flowcharts, flowchartDataResults.data, () => {
    props.refetchParent();
    flowchartStructureResults.refetch();
    flowchartDataResults.refetch();
  });
}

export interface LiveUpdatingTaskFlowchartWrapperProps {
  parentId: string;
  flowchartIds: Array<string>;
  processTaskIds: Array<string>;
  children: (
    flowcharts: Array<ProcessFlowchartStructure>,
    flowchartData: ProcessFlowchartDataQuery,
    forceRefetch: () => void
  ) => JSX.Element | null;
  refetch: () => Promise<unknown>;
}

export default function LiveUpdatingTaskFlowchartWrapper(
  props: LiveUpdatingTaskFlowchartWrapperProps
): JSX.Element {
  return (
    <NonPollingFlowchartWrapper
      flowchartIds={props.flowchartIds}
      processTaskIds={props.processTaskIds}
      refetchParent={props.refetch}
    >
      {(flowcharts, flowchartData, forceRefetch) => {
        let hasIncompleteData = false;
        if (flowchartData.processTasks.length > 0 && !isInaccessible(flowchartData.processTasks[0])) {
          hasIncompleteData = flowchartData.processTasks[0].isMappingInProgressForFlowchartRootedAtTask;
        }
        return (
          <ProcessRealtimeUpdateSubscriptionWrapper
            apiId={props.parentId}
            messageFilter="no_filter"
            refetch={forceRefetch}
            refetchOnMessagesFromSameClient={true}
            hasIncompleteDataShouldRefetch={hasIncompleteData}
          >
            {props.children(flowcharts, flowchartData, forceRefetch)}
          </ProcessRealtimeUpdateSubscriptionWrapper>
        );
      }}
    </NonPollingFlowchartWrapper>
  );
}
